import React, { useState } from 'react'
import { Popover, QRCode, Spin } from 'antd';
import jsCookie from 'js-cookie'
import { useRootStore } from '@/contexts';
import { LangText } from '@/components'
import { JAPAN_MALL_URL, VIP_URL, SAGAWA_URL, TOKEN_KEY } from '@/constants'
import { apiAuthAuthorize } from '@/service/api/authAuthorize'
import { PlatformEnum } from '@/service/api/types'
import { useTranslation } from 'react-i18next'
import i18nInstance from '@/i18n'

type OutLinkClickOptions = {
  platform: PlatformEnum
  linUrl?: string
  language?: string
}

const getPlatformUrl = (platform: PlatformEnum, linUrl?: string) => {
  switch (platform) {
    case PlatformEnum.JAPANMALL:
      return linUrl || JAPAN_MALL_URL
    case PlatformEnum.VIP:
      return linUrl || VIP_URL
    case PlatformEnum.SGW:
      return linUrl || SAGAWA_URL
    default:
      return linUrl || ''
  }
}

const getAuthorizedLink = async ({ platform, linUrl, language }: OutLinkClickOptions) => {
  const link = getPlatformUrl(platform, linUrl)

  if (!link) return ''

  const targetUrl = new URL(link)
  targetUrl.searchParams.set('lang', language || i18nInstance.language || 'zh_CN')

  // 登录态才换 ticket，供目标项目免登；未登录不鉴权、不传 ticket
  if (jsCookie.get(TOKEN_KEY)) {
    const { data } = await apiAuthAuthorize({ platform })
    targetUrl.searchParams.set('ticket', data.ticket || '')
  }
  return targetUrl.toString()
}

/** 跳转到对应业务系统；登录态会附带 ticket 免登。 */
export const handleClick = async (options: OutLinkClickOptions) => {
  const link = await getAuthorizedLink(options)

  if (link) {
    window.open(link, '_blank', 'noopener,noreferrer')
  }

  return link
}

interface QRCodePopoverProps {
  children: React.ReactElement;
  platform: PlatformEnum
  linUrl?: string
}
const QRCodePopover = (props: QRCodePopoverProps) => {
  const { children, platform, linUrl } = props;
  const { userStore } = useRootStore()
  const { user } = userStore
  const [open, setOpen] = useState(false)
  const [qrCodeValue, setQrCodeValue] = useState('')
  const [loading, setLoading] = useState(false)
  const { i18n } = useTranslation()

  const handleOutLinkClick = async () => {
    setLoading(true)
    try {
      if (platform === PlatformEnum.JAPANMALL) {
        await handleClick({ platform, linUrl, language: i18n.language })
        return
      }

      const link = await getAuthorizedLink({ platform, linUrl, language: i18n.language })
      setQrCodeValue(link)
      setOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const popoverContent = (
    <div className="flex w-[280px] flex-col items-center">
      <LangText
        lngKey="outLink.qr.scanPrompt"
        className="text-center text-[18px] font-bold leading-[26px] text-[#222]"
      />
      {user && (
        <LangText
          lngKey="outLink.qr.personalUseWarning"
          className="mt-[10px] rounded-[8px] bg-[#FFF1F0] px-[12px] py-[8px] text-center text-[14px] leading-[20px] text-[#FF4D4F]"
        />
      )}
      <div className="mt-[18px] rounded-[14px] border border-[#E8F1FF] bg-[#F7FAFF] p-[16px]">
        <QRCode
          value={qrCodeValue || '-'}
          size={200}
          bordered={false}
          color="#1F1F1F"
          bgColor="transparent"
        />
      </div>
      <span className="mt-[14px] h-[3px] w-[40px] rounded-full bg-primary/80" />
    </div>
  )

  return (
    <Spin spinning={loading}>
      <Popover
        content={popoverContent}
        trigger="click"
        open={open}
        placement="top"
        arrow={{ pointAtCenter: true }}
        styles={{
          container: {
            padding: '20px 22px',
            borderRadius: 18,
            border: '1px solid #E8F1FF',
            boxShadow: '0 12px 32px rgba(50, 139, 248, 0.14)',
          },
        }}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setOpen(false)
        }}
      >
        {React.cloneElement(children, {
          onClick: handleOutLinkClick,
        })}
      </Popover>
    </Spin>
  )
}

export default QRCodePopover